"use client";
import { jsx } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from "react";
import { useSafeInView } from "../../hooks/index.js";
import { buildContentfulImageUrl } from "../ContentfulImgApi/buildContentfulImageUrl.js";
import { Image } from "../Image/Image.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import { getOptimizationParams, parseRatio } from "./optimizationConfig.js";
import { WidthCapContext } from "./WidthCapContext.js";
function findContainerAncestor(el) {
    let current = el.parentElement;
    while(current){
        const ct = getComputedStyle(current).containerType;
        if ('inline-size' === ct || 'size' === ct) return current;
        current = current.parentElement;
    }
    return null;
}
function resolveServeHeight(objectFit, ratio, serveWidth, fixedHeight, displayHeight) {
    if (null == objectFit || 'cover' === objectFit) {
        const parsedRatio = parseRatio(ratio);
        if (null == parsedRatio) {
            const fallbackH = fixedHeight ?? (displayHeight > 0 ? displayHeight : void 0);
            return null == fallbackH ? void 0 : Math.round(2 * fallbackH);
        }
        return Math.round(serveWidth / parsedRatio);
    }
}
const ContentfulImgOptimizer = ({ contentfulUrl, fixedWidth, fixedHeight, widthCap, ratio, objectFit = 'cover', height, ...imageProps })=>{
    const wrapperRef = useRef(null);
    const contextWidthCap = useContext(WidthCapContext);
    const effectiveWidthCap = widthCap ?? contextWidthCap;
    const [dimensions, setDimensions] = useState(null == fixedWidth ? null : {
        w: fixedWidth,
        h: fixedHeight ?? 0
    });
    useEffect(()=>{
        null == fixedWidth ? setDimensions(null) : setDimensions({
            w: fixedWidth,
            h: fixedHeight ?? 0
        });
    }, [
        fixedWidth,
        fixedHeight
    ]);
    const contentfulUrlRef = useRef(contentfulUrl);
    const effectiveWidthCapRef = useRef(effectiveWidthCap);
    useEffect(()=>{
        contentfulUrlRef.current = contentfulUrl;
        effectiveWidthCapRef.current = effectiveWidthCap;
    }, [
        contentfulUrl,
        effectiveWidthCap
    ]);
    const committedServeWidth = useRef(0);
    useEffect(()=>{
        committedServeWidth.current = 0;
    }, [
        contentfulUrl,
        fixedWidth
    ]);
    const isInView = useSafeInView(wrapperRef, {
        once: true,
        margin: '200px'
    });
    useEffect(()=>{
        if (null != fixedWidth) return;
        if (!isInView) return;
        const el = wrapperRef.current;
        if (!el) return;
        let debounceTimer;
        const observer = new ResizeObserver((entries)=>{
            const entry = entries[0];
            if (!entry) return;
            const w = entry.contentRect.width;
            const h = entry.contentRect.height;
            if (w <= 0) return;
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(()=>{
                const capSetting = effectiveWidthCapRef.current;
                let cap;
                if ('container' === capSetting) {
                    const ancestor = findContainerAncestor(el);
                    cap = null == ancestor ? void 0 : ancestor.offsetWidth;
                } else cap = capSetting;
                const displayWidth = null == cap ? w : Math.min(w, cap);
                const { serveWidth: newServeWidth } = getOptimizationParams(displayWidth, contentfulUrlRef.current);
                if (committedServeWidth.current > 0 && newServeWidth <= committedServeWidth.current) return;
                committedServeWidth.current = newServeWidth;
                setDimensions({
                    w,
                    h,
                    cap
                });
            }, 300);
        });
        observer.observe(el);
        return ()=>{
            clearTimeout(debounceTimer);
            observer.disconnect();
        };
    }, [
        fixedWidth,
        isInView
    ]);
    const heightClass = 'full' === height ? 'h-full' : '';
    let imageElement = /*#__PURE__*/ jsx(Skeleton, {
        type: "image",
        width: "100%",
        height: "100%",
        animate: true
    });
    if (null !== dimensions) {
        const rawWidth = dimensions.w;
        const rawHeight = dimensions.h;
        const resolvedCap = dimensions.cap ?? ('number' == typeof effectiveWidthCap ? effectiveWidthCap : void 0);
        const displayWidth = null == resolvedCap ? rawWidth : Math.min(rawWidth, resolvedCap);
        const displayHeight = rawHeight;
        const { serveWidth, quality } = getOptimizationParams(displayWidth, contentfulUrl);
        const serveHeight = resolveServeHeight(objectFit, ratio, serveWidth, fixedHeight, displayHeight);
        const optimizedSrc = buildContentfulImageUrl(contentfulUrl, {
            w: serveWidth,
            ...null == serveHeight ? {} : {
                h: serveHeight,
                fit: 'fill'
            },
            fm: 'avif',
            q: quality
        });
        imageElement = /*#__PURE__*/ jsx(Image, {
            src: optimizedSrc,
            ratio: ratio,
            objectFit: objectFit,
            height: height,
            ...imageProps
        });
    }
    return /*#__PURE__*/ jsx("div", {
        ref: wrapperRef,
        className: `w-full ${heightClass}`,
        style: ratio && 'auto' !== ratio ? {
            aspectRatio: ratio
        } : void 0,
        children: imageElement
    });
};
export { ContentfulImgOptimizer };
